import * as React from 'react';
import * as Yup from 'yup';
import { Field, Formik, FieldProps, FormikProps, FormikValues } from 'formik';
import { omit } from 'lodash';
import {
  StyledForm,
  FieldRow,
  StyledFormGroup,
  StyledFormSelect,
  Card,
  Button,
  NewHeroImageCrop as NewHeroImage,
  StyledTextArea,
} from '../../components';
import Span from '../../typography/Span';
import { formatFormValues, formatGqlError } from '../../utils/helpers';
import { MutationFn } from 'react-apollo';
import { SelectObject, ImageObject } from '../../utils/types';
import { PostType, StakeholderRole, PostStatus } from '../../generated/graphql';
import { ApolloError } from 'apollo-client';
import { withRouter, RouteComponentProps } from 'react-router';
import { ValidateCharacterLimit } from './../../utils/helpers';
import PdfUploadListField from '../../components/PdfUploadListField';
// import NewFile from '../../components';

const STATUS_OPTIONS: SelectObject[] = [
  { label: 'Draft', value: PostStatus.Draft },
  { label: 'Published', value: PostStatus.Published },
  { label: 'Archived', value: PostStatus.Archived },
];

const CreateNewsArticleValidation = Yup.object().shape({
  title: Yup.mixed().required('Required'),
  body: Yup.string().required('Required'),
  roles: Yup.mixed(),
  status: Yup.mixed().required('Required'),
});

const OWNER_ROLE = { label: 'Owner', value: 'OWNER' };

type NewsArticleFormValues = {
  title?: string;
  category?: SelectObject[];
  body?: string;
  roles?: SelectObject[];
  status?: SelectObject;
  id?: string;
  type?: PostType;
  /**
   * PDF list persisted on `Post.pdfs` as a JSON stringified array of `{ name, link }`.
   */
  pdfs?: string;
  /**
   * Current PDF name used by the upload modal.
   */
  pdfName?: string;
};

interface Props extends RouteComponentProps {
  mutation: MutationFn;
  error?: ApolloError;
  buttonText?: string;
  loading: boolean;
  isNewPost?: boolean;
  initialValues: FormikValues;
  type?: PostType;
  roles?: StakeholderRole;
  image?: ImageObject;
}

function getMultipleCategories(roles: any[]): any[] {
  return roles.map(
    (role) =>
      role && {
        value: role.value,
        label: role.label === 'Guest ' ? 'Public' : role.label,
      }
  );
}

function formatInitialvalues(values: any): null | FormikValues {
  if (!values) {
    return null;
  }
  return {
    ...values,
    roles: values.roles ? getMultipleCategories(values.roles) : null,
  };
}

const NewsArticleForm: React.FC<Props> = ({
  type,
  initialValues,
  error,
  loading,
  mutation,
  isNewPost,
  image,
  history,
  ...rest
}) => {
  const [uploadedImage, setUploadedImage] = React.useState<ImageObject | null>(image || null);

  const renderPostImage = (): any => {
    if (isNewPost) {
      return (
        <div>
          <NewHeroImage
            className="ContentManagementAddImage"
            uploadText="ADD IMAGE"
            onUploadComplete={(data: ImageObject | null) => {
              if (!data) {
                return;
              }
              setUploadedImage({
                original: data.original || data.scaled,
                scaled: data.scaled,
                thumbnail: data.thumbnail,
              });
            }}
            allowedFileTypes={['image/*']}
            modalHeading="Upload Image"
          />
        </div>
      );
    }

    return (
      <NewHeroImage
        className="ContentManagementAddImage"
        uploadText="EDIT IMAGE"
        src={image && (image.scaled || image.original)}
        onUploadComplete={(data: ImageObject | null) => {
          if (!data) {
            return;
          }
          setUploadedImage({
            original: data.original || data.scaled,
            scaled: data.scaled,
            thumbnail: data.thumbnail,
          });
        }}
        allowedFileTypes={['image/*']}
        modalHeading="Upload Image"
      />
    );
  };

  return (
    <Formik
      validationSchema={CreateNewsArticleValidation}
      enableReinitialize={true}
      initialValues={
        initialValues
          ? {
              ...(formatInitialvalues(initialValues) as any),
              pdfs: (initialValues as any).pdfs || '',
              pdfName: (initialValues as any).pdfName || '',
            }
          : {
              title: undefined,
              body: undefined,
              category: undefined,
              roles: [OWNER_ROLE],
              status: undefined,
              id: undefined,
              pdfs: '',
              pdfName: '',
            }
      }
      onSubmit={(values: NewsArticleFormValues): any => {
        // `type` is provided on create, but not passed on edit; edit relies on `GetPost` to hydrate it.
        const postType = type || (values as any).type;
        const formattedValues = formatFormValues(values);
        const cleanValues = omit(formattedValues, ['image']) as {
          title?: string;
          body?: string;
          status?: string;
        };

        const variables = {
          data: {
            title: cleanValues.title,
            body: cleanValues.body,
            status: cleanValues.status,
            type: postType,
            pdfs: values.pdfs || JSON.stringify([]),
            image: uploadedImage
              ? {
                  scaled: uploadedImage.original,
                  original: uploadedImage.original,
                  thumbnail: uploadedImage.original,
                }
              : undefined,
          },
        };

        console.log('variables', variables);
        mutation({ variables });
      }}
      render={(formikProps: FormikProps<NewsArticleFormValues>): any => {
        return (
          <StyledForm flexDirection="row" flexWrap="wrap">
            <Card flex={[1, 1, 3]} bg="white" p={4}>
              <FieldRow>
                <Field
                  name="title"
                  render={({ field }: FieldProps<NewsArticleFormValues>): any => (
                    <StyledFormGroup {...field} field={field} form={formikProps} label="Title" />
                  )}
                />
              </FieldRow>
              <FieldRow>
                <Field
                  name="body"
                  validate={ValidateCharacterLimit}
                  render={({ field }: FieldProps<NewsArticleFormValues>): any => (
                    <StyledTextArea {...field} field={field} form={formikProps} label="Body" />
                  )}
                />
              </FieldRow>
            </Card>
            <Card ml={[0, 0, 4]} flex={[1, 1, 1]} minWidth="300px" bg="white" p={4}>
              {renderPostImage()}
              {((type || (formikProps.values as any).type) === PostType.News ||
                (type || (formikProps.values as any).type) === PostType.Update ||
                (type || (formikProps.values as any).type) === PostType.Newsletter) && (
                <PdfUploadListField
                  formikProps={formikProps}
                  pdfsField="pdfs"
                  pdfNameField="pdfName"
                />
              )}
              {/* <Field
                name="roles"
                render={({ field }: FieldProps<NewsArticleFormValues>): any => (
                  <StyledFormSelect
                    {...field}
                    field={field}
                    isMulti={true}
                    label="Stakeholders"
                    form={formikProps}
                    options={MULTI_SELECT_STAKEHOLDER_ROLES}
                    isDisabled={true}
                  />
                )}
              /> */}
              <Field
                name="status"
                render={({ field }: FieldProps<NewsArticleFormValues>): any => (
                  <StyledFormSelect
                    {...field}
                    field={field}
                    label="Status"
                    form={formikProps}
                    options={STATUS_OPTIONS}
                  />
                )}
              />
              <Field
                name="id"
                render={({ field }: FieldProps<NewsArticleFormValues>): any => (
                  <StyledFormGroup
                    {...field}
                    field={field}
                    form={formikProps}
                    label="Unique ID"
                    placeholder="Unique ID"
                    disabled={true}
                  />
                )}
              />
              {error && (
                <Span width="100%" textAlign="center" color="error" mb={3}>
                  {formatGqlError(error)}
                </Span>
              )}
              <Button mb={1} type="submit" text="SAVE & UPDATE" isLoading={loading} />
            </Card>
          </StyledForm>
        );
      }}
    />
  );
};

export default withRouter(NewsArticleForm);



// WEBPACK FOOTER //
// ./src/forms/contentManagement/NewsArticleForm.tsx